/**
 * MUI Scale Component
 * 
 * This component is the Material UI equivalent of the original Scale component.
 * It renders a color scale for heatmaps in the Master Designer application.
 */

import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Box, Divider, styled, Typography, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { getColorGradient } from 'reduxModules/ducks/ui';
import { InputLabel } from '../atoms';
import Canvas from '../atoms/Canvas';
import Button from '../molecules/Button';
import Info from '../atoms/Info';
import { getArea, getPlotProps, getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { getDrawingBox, getCurrentView, getLastDeviceView, getIsMeter } from 'reduxModules/ducks/ui';
import { range2Value, value2Range, getLabels, getSplTickValues } from 'utilities/color';
import { toPairs } from 'lodash';
import { ScaleProps } from 'types/components/molecules';

/**
 * Interface for plot properties
 */
interface PlotProperties {
  label: string;
  units?: string;
  info?: string;
  average?: number | null;
  deviation?: number | null;
  minLevel: number;
  maxLevel: number;
  accuracy?: number;
  plotType?: string;
  [key: string]: any;
}

/**
 * Styled component for the Scale wrapper
 */
const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'legendWidth',
})<{ legendWidth?: number }>(({ theme, legendWidth }) => ({
  width: legendWidth ? `${legendWidth}px` : '75%',
  paddingBottom: '4px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginTop: '-20px',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

/**
 * Styled component for the scale content row
 */
const ScaleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxHeight: '35px',
}));

/**
 * MUI Scale component
 * 
 * @returns {JSX.Element} The MUI Scale component
 */
const MuiScale: React.FC<ScaleProps> = () => {
  const muiTheme = useTheme();
  const plotProps = useSelector(getPlotProps) as PlotProperties;
  const inArea = useSelector(getArea);
  const currentView = useSelector(getCurrentView as (state: any) => string);
  const lastDeviceView = useSelector(getLastDeviceView as (state: any) => string);
  const currentDevice = useSelector((state: any) => (getCurrentValue2 as any)(state, 'currentDevice'));
  const cameraModel = useSelector((state: any) => (getCurrentValue2 as any)(state, 'cameras.model'));
  const isMeter = useSelector(getIsMeter);
  const { bbox } = useSelector(getDrawingBox) as { bbox: { width: number } };
  
  // Set default plot properties for device views when plotProps is empty
  // For acoustics view, use the last device view that was opened
  const effectiveView = currentView === 'acoustics' && lastDeviceView ? lastDeviceView : currentView;
  
  const defaultPlotProps: Partial<PlotProperties> = React.useMemo(() => {
    if (effectiveView === 'microphones') {
      return {
        label: 'Distance from nearest microphone',
        plotType: 'micDistance',
        minLevel: 0,
        maxLevel: 6,
        accuracy: 100,
        units: isMeter ? 'Meters' : 'Feet'
      };
    } else if (effectiveView === 'camera') {
      // Determine label based on camera model
      const cameraLabel = cameraModel === 'vidi280' 
        ? 'Vidi 280 Dual-Imager Coverage' 
        : cameraModel === 'vidi250'
        ? 'Vidi 250 Coverage'
        : 'Camera Coverage';
      
      return {
        label: cameraLabel,
        plotType: 'cameraCoverage',
        minLevel: 0,
        maxLevel: isMeter ? 9 : 9.144, // ~30 feet
        accuracy: 100,
        units: isMeter ? 'Meters' : 'Feet'
      };
    } else if (effectiveView === 'speakers') {
      return {
        label: 'Relative Average Level',
        plotType: 'relativeAverage',
        minLevel: -10,
        maxLevel: 4,
        accuracy: 100,
        units: 'dB'
      };
    }
    return {};
  }, [effectiveView, isMeter]);
  
  // Merge default props with actual props, giving precedence to actual props
  const mergedPlotProps = { ...defaultPlotProps, ...plotProps } as PlotProperties;

  // Get gradient from selector or create a default one for device views
  const selectorGradient = useSelector(getColorGradient);
  
  const gradientRGBA = React.useMemo(() => {
    // Use selector gradient if available
    if (selectorGradient && Object.keys(selectorGradient).length > 0) {
      return selectorGradient;
    }
    
    return {};
  }, [selectorGradient, effectiveView]);

  // Determine if we should show a legend bar based on the current view
  // Show legend bar for device views or for acoustics when there's a last device view
  const showLegendBar = currentView === 'microphones' || currentView === 'speakers' || currentView === 'camera' ||
    (currentView === 'acoustics' && lastDeviceView);

  /**
   * Effect to draw the color scale on the canvas
   */
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (canvasRef.current) {
      const canvasWidth = canvasRef.current.offsetWidth; // Use full width
      const ctx = canvasRef.current.getContext('2d');
      
      if (!ctx) return; // Early return if context is not available
      
      ctx.canvas.width = canvasWidth;
      ctx.canvas.height = 30; // Height for the gradient bar with labels inside
      ctx.clearRect(0, 0, canvasWidth, 30);
      
      // Draw gradient background that fills entire canvas
      // Use the gradientRGBA from selector
      for (let x = 0; x <= canvasWidth; x++) {
        const colorIndex = range2Value(mergedPlotProps as any, canvasWidth, x);
        const rgbaColor = gradientRGBA[Math.floor(colorIndex * (mergedPlotProps.accuracy || 100))];
        
        const rgbaString = typeof rgbaColor === 'object' && rgbaColor.length >= 3 
          ? `rgba(${rgbaColor.join(',')})` 
          : 'rgba(0,0,0,0)';
          
        ctx.strokeStyle = rgbaString;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, 30);
        ctx.stroke();
      }
      
      // Draw labels centered inside the gradient bar
      // Use white text in dark mode, black in light mode
      ctx.fillStyle = muiTheme.palette.mode === 'dark' ? "#ffffff" : "#000000";
      ctx.font = "12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle"; // Center labels vertically
      
      if (effectiveView === 'microphones') {
        // Distance-based legend for microphones (feet/meters)
        const { minLevel = 0, maxLevel = 6 } = mergedPlotProps;
        
        if (isMeter) {
          // Metric: dynamically calculate labels based on maxLevel
          let distances: number[] = [];
          
          if (maxLevel === 12) {
            // Bar microphones: show 1m-12m range with specific intervals
            distances = [1, 3, 5, 7, 9, 11, 12];
          } else if (maxLevel <= 6) {
            // For small ranges (normal mics), show 1m, 2m, 3m, 4m, 5m, 6m
            distances = [1, 2, 3, 4, 5, 6].filter(d => d <= maxLevel);
          } else if (maxLevel <= 15) {
            // For medium ranges, use 2m or 3m intervals
            const interval = maxLevel <= 10 ? 2 : 3;
            for (let i = interval; i <= maxLevel; i += interval) {
              distances.push(i);
            }
          } else {
            // For large ranges, use evenly spaced intervals
            const numLabels = 6; // Show 6 evenly spaced labels
            const interval = Math.ceil(maxLevel / numLabels / 5) * 5; // Round to nearest 5
            for (let i = interval; i <= maxLevel; i += interval) {
              distances.push(i);
            }
            // Ensure we don't have too many labels
            if (distances.length > 8) {
              distances = distances.filter((_, index) => index % 2 === 0);
            }
          }
          
          distances.forEach(distance => {
            let xPos = value2Range(mergedPlotProps as any, canvasWidth, distance);
            
            // Adjust position for the last label to prevent cutoff
            if (distance === maxLevel) {
              // Measure the text width and adjust position to keep it within canvas
              const textWidth = ctx.measureText(`${distance}m`).width;
              xPos = Math.min(xPos, canvasWidth - textWidth / 2 - 2); // 2px padding
            }
            
            // Use 'm' for meters instead of single quote
            ctx.fillText(`${distance}m`, xPos, 15); // Center at half of 30px height
          });
        } else {
          // Imperial: dynamically calculate labels based on maxLevel
          const maxDistanceFeet = Math.round(maxLevel * 3.28084);
          let distances: number[] = [];
          
          if (maxLevel === 12) {
            // Bar microphones: show 3'-39' range with specific intervals
            distances = [3, 9, 15, 21, 27, 33, 39];
          } else if (maxDistanceFeet <= 20) {
            // For small ranges (normal mics), use 3', 6', 9', 12', 15', 18'
            distances = [3, 6, 9, 12, 15, 18].filter(d => d <= maxDistanceFeet);
          } else if (maxDistanceFeet <= 50) {
            // For medium ranges, use 10' intervals
            distances = [10, 20, 30, 40, 50].filter(d => d <= maxDistanceFeet);
          } else {
            // For large ranges, use evenly spaced intervals
            const numLabels = 6; // Show 6 evenly spaced labels
            const interval = Math.ceil(maxDistanceFeet / numLabels / 10) * 10; // Round to nearest 10
            for (let i = interval; i <= maxDistanceFeet; i += interval) {
              distances.push(i);
            }
            // Ensure we don't have too many labels
            if (distances.length > 8) {
              distances = distances.filter((_, index) => index % 2 === 0);
            }
          }
          
          distances.forEach(distanceFeet => {
            const distanceMeters = distanceFeet / 3.28084; // Convert to meters for position calculation
            let xPos = (distanceMeters / maxLevel) * canvasWidth; // Direct proportion calculation
            
            // Adjust position for the last label to prevent cutoff
            if (distanceFeet === 39) { // Bar microphone max distance in feet
              // Measure the text width and adjust position to keep it within canvas
              const textWidth = ctx.measureText(`${distanceFeet}'`).width;
              xPos = Math.min(xPos, canvasWidth - textWidth / 2 - 2); // 2px padding
            }
            
            // Draw label centered in the bar
            ctx.fillText(`${distanceFeet}'`, xPos, 15); // Center at half of 30px height
          });
        }
      } else if (effectiveView === 'camera') {
        // Distance-based legend for camera (feet/meters)
        const { minLevel = 0, maxLevel = 9.144 } = mergedPlotProps;
        
        if (isMeter) {
          // Metric: show distances in meters
          const distances = [1, 2, 3, 4, 5, 6, 7, 8, 9];
          distances.forEach(distance => {
            if (distance <= maxLevel) {
              const xPos = value2Range(mergedPlotProps as any, canvasWidth, distance);
              ctx.fillText(`${distance}m`, xPos, 15);
            }
          });
        } else {
          // Imperial: show distances in feet
          const distances = [5, 10, 15, 20, 25, 30];
          distances.forEach(distanceFeet => {
            const distanceMeters = distanceFeet / 3.28084; // Convert to meters
            if (distanceMeters <= maxLevel) {
              const xPos = value2Range(mergedPlotProps as any, canvasWidth, distanceMeters);
              ctx.fillText(`${distanceFeet}'`, xPos, 15);
            }
          });
        }
      } else if (effectiveView === 'speakers') {
        // For speakers with relative level plot
        if (mergedPlotProps.plotType === 'relativeAverage') {
          // Relative level legend (-10dB to +4dB)
          const tickValues = [-10, -8, -6, -4, -2, 0, 2, 4];
          tickValues.forEach(value => {
            const xPos = value2Range(mergedPlotProps as any, canvasWidth, value);

            // Draw all labels with dB suffix
            const label = value >= 0 ? `${value}dB` : `${value}dB`;
            ctx.fillText(label, xPos, 15); // Center at half of 30px height
          });
        } else if (mergedPlotProps.plotType === 'STI') {
          // STI - use the standard labels (BAD, POOR, FAIR, GOOD, EXCELLENT)
          const labels = getLabels(mergedPlotProps as any);
          toPairs(labels).forEach(([key, value]) => {
            const xPosition = value2Range(mergedPlotProps as any, canvasWidth, value as number);
            ctx.fillText(key, xPosition, 15);
          });
        } else if (mergedPlotProps.plotType === 'SPL') {
          // SPL (Direct/Total) - draw dBA tick labels across the min->max range
          // so the scale shows numbers like the Relative Average Level plot.
          // Bounds come from getPlotProps (derived from the SPL data). (PIG-215)
          const { minLevel = 60, maxLevel = 100, units = 'dBA' } = mergedPlotProps as any;
          getSplTickValues(minLevel, maxLevel).forEach(value => {
            const label = `${value}${units}`;
            const textWidth = ctx.measureText(label).width;
            // Clamp edge labels so the first/last stay within the canvas.
            const xPos = Math.max(
              textWidth / 2 + 2,
              Math.min(value2Range(mergedPlotProps as any, canvasWidth, value), canvasWidth - textWidth / 2 - 2),
            );
            ctx.fillText(label, xPos, 15); // Center at half of 30px height
          });
        }
      } else {
        // Default labels for other views
        const labels = getLabels(mergedPlotProps as any);
        toPairs(labels).forEach(([key, value]) => {
          const xPosition = value2Range(mergedPlotProps as any, canvasWidth, value as number);
          ctx.fillText(key, xPosition, 28);
        });
      }
    }
  });

  const theme = {
    labelWidth: 'auto',
    labelAlign: 'start',
    overflow: 'visible',
  };

  const [isOpen, setIsOpen] = useState(false);
  const onMouseDown = () => {
    setIsOpen(true);
  };

  const onMouseUp = () => {
    setIsOpen(false);
  };

  // Customize label and units based on current view
  const { info, average, deviation } = mergedPlotProps;
  let { label, units } = mergedPlotProps;
  
  // Override label and units for specific device views
  if (effectiveView === 'microphones') {
    label = 'Distance from nearest microphone';
    units = isMeter ? 'Meters' : 'Feet';
  } else if (effectiveView === 'camera') {
    const unitLabel = isMeter ? 'Meters' : 'Feet';
    label = `Distance from camera (${unitLabel})`;
    units = unitLabel;
  } else if (effectiveView === 'speakers') {
    if (mergedPlotProps.plotType === 'relativeAverage') {
      label = 'Relative Average Level';
      units = 'dB';
    }
  }
  
  const showAverage = inArea && average;

  // Don't render if not in a view that needs a legend and no plot props
  if (!showLegendBar && !mergedPlotProps.label) {
    return null;
  }

  return (
    <StyledBox legendWidth={showLegendBar ? bbox?.width : undefined}>
      {label && (
        <Typography
          variant="body2"
          sx={{ 
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'text.primary',
            alignSelf: 'flex-start',
            paddingLeft: 0
          }}
        >
          {label}
        </Typography>
      )}
      <ScaleRow>
        <Canvas 
          id="scale" 
          ref={canvasRef} 
          legend={true} 
          style={{ width: '100%' }}
        />
        {units && !showLegendBar && (
          <Typography 
            variant="body2" 
            className="text-nowrap ms-1" 
            sx={{ color: 'text.primary', marginLeft: '8px' }}
          >
            {units}
          </Typography>
        )}
        {info && (
          <Button
            label=""
            name={'help'}
            state={'button_active'}
            place={'top'}
            editable={false}
            theme={{}}
            onClick={() => {}}
            bubble={info}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
          />
        )}
        <Info id={'help'} msg={info || ''} />
        {showAverage && (
          <Fragment>
            <Divider orientation="vertical" flexItem className="mx-2" />
            <InputLabel className="text-nowrap" sx={{ color: 'text.primary' }}>AVG: {average}</InputLabel>
            <InputLabel className="text-nowrap" sx={{ color: 'text.primary' }}>&nbsp;&plusmn; {deviation}</InputLabel>
            <InputLabel className="text-nowrap" sx={{ color: 'text.primary' }}>&nbsp;{units}</InputLabel>
          </Fragment>
        )}
      </ScaleRow>
    </StyledBox>
  );
};

export default MuiScale;
